import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sparkles, Heart, Users, HandHeart, ShieldCheck, Video, Globe2, Sprout } from "lucide-react";
import { motion } from "framer-motion";
import { COUNTRIES, COUNTRY_CODES, countryName } from "@/constants/countries";

const HERO_IMG = "/uploads/blog/cover-hero-inicio-2026-1788733208.png";

/**
 * Copy hero localizado por país. La forma preposicional cambia por país
 * (ej: "en México", "en Argentina", "en España"). Sin `?pais=`, el hero
 * queda universal ("Conoce gente en Latinoamérica que vive sin alcohol...").
 */
function heroCopyFor(country) {
  if (!country) {
    return {
      lead: "Conoce gente",
      middle: "en Latinoamérica",
      accent: "que vive sin",
      tail: "alcohol ni drogas.",
    };
  }
  const name = countryName(country);
  return {
    lead: "Conoce gente",
    middle: `en ${name}`,
    accent: "que vive sin",
    tail: "alcohol ni drogas.",
  };
}

export default function Landing() {
  const routerLoc = useLocation();
  const [country, setCountry] = useState(null);

  // 1) ?pais=XX de la URL (source: URL — para tráfico pagado por país).
  // 2) Sin ?pais= el H1 queda universal: "Conoce gente en Latinoamérica".
  //    (Antes detectábamos por Accept-Language/IP, pero eso a veces mostraba
  //    España en tráfico incógnito con locale es-ES por default → resta claridad
  //    del posicionamiento LATAM.)
  // También persistimos ?pais + UTM en localStorage.ps_acquisition para
  // sobrevivir al click a /registro (que no arrastra los query params).
  useEffect(() => {
    const params = new URLSearchParams(routerLoc.search);
    const pais = (params.get("pais") || "").toUpperCase().trim();

    // Persist acquisition ANTES de definir país — así funciona igual con o sin ?pais
    const acquisition = {
      pais: pais && COUNTRY_CODES.includes(pais) ? pais : undefined,
      utm_source:   params.get("utm_source")   || undefined,
      utm_medium:   params.get("utm_medium")   || undefined,
      utm_campaign: params.get("utm_campaign") || undefined,
      utm_content:  params.get("utm_content")  || undefined,
    };
    if (Object.values(acquisition).some(Boolean)) {
      try { localStorage.setItem("ps_acquisition", JSON.stringify(acquisition)); } catch (_e) { /* ignore */ }
    }

    if (pais && COUNTRY_CODES.includes(pais)) {
      setCountry(pais);
    }
  }, [routerLoc.search]);

  const copy = heroCopyFor(country);
  // Bandera del país cuando hay ?pais=XX; globo 🌎 cuando es genérico LATAM.
  const flag = country ? (COUNTRIES.find((c) => c.code === country)?.flag || "") : "🌎";
  return (
    <div className="min-h-screen bg-[#0E0F13] text-white relative overflow-hidden">
      {/* background glow */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle, #FF6B5E 0%, transparent 60%)" }} />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle, #8B5CF6 0%, transparent 60%)" }} />

      <div className="mx-auto max-w-md px-6 pt-10 pb-10 relative z-10">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-2xl ps-gradient flex items-center justify-center">
            <Sparkles size={22} className="text-white" />
          </div>
          <span className="font-display text-xl font-black tracking-tight">PlanSobrio</span>
          <span
            data-testid="beta-badge"
            className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-wider"
            style={{ borderColor: "#4ADE80", color: "#4ADE80" }}
          >
            Beta
          </span>
        </div>

        {/* Hero image — grupo diverso, café en mano, sin alcohol */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="relative rounded-3xl overflow-hidden mb-8 aspect-[4/3] border border-white/10 shadow-2xl shadow-purple-500/10"
        >
          <img
            src={HERO_IMG}
            alt="Grupo de amigos disfrutando una tarde con café y jugo, sin alcohol"
            data-testid="landing-hero-img"
            loading="eager"
            fetchpriority="high"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Bottom gradient para legibilidad del badge flotante */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E0F13]/85 via-[#0E0F13]/10 to-transparent"/>
          <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 backdrop-blur border border-white/20 px-2.5 py-1 text-[11px] font-bold">
              <Globe2 size={11} strokeWidth={2.4}/> 22 países hispanohablantes
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 backdrop-blur border border-white/20 px-2.5 py-1 text-[11px] font-bold">
              <ShieldCheck size={11} strokeWidth={2.4} className="text-[#4ADE80]"/> Gratis, sin ads
            </span>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="font-display text-4xl sm:text-5xl font-black leading-[1.02] tracking-tight"
          data-testid="landing-hero-h1"
        >
          {copy.lead}<br/>
          {copy.middle && (
            <>
              <span data-testid="landing-hero-country" className="text-white">
                {copy.middle} {flag && <span aria-hidden="true">{flag}</span>}
              </span>
              <br/>
            </>
          )}
          <span className="ps-gradient-text">{copy.accent}</span><br/>{copy.tail}
        </motion.h1>

        <p className="mt-5 text-white/70 text-[15px] leading-relaxed">
          Encuentra amigos, pareja, apoyo o un grupo para hacer planes reales — sin que el trago sea el centro. La primera comunidad hispana pensada para quien elige otra forma de pasarlo bien.
        </p>

        <div className="mt-7 space-y-3">
          <Link to="/registro" data-testid="landing-register-btn" className="ps-btn-primary block text-center text-base">
            Crear cuenta gratis
          </Link>
          <Link to="/login" data-testid="landing-login-btn" className="ps-btn-secondary block text-center text-base">
            Ya tengo cuenta
          </Link>
        </div>

        {/* Los 4 modos */}
        <p className="mt-10 mb-3 text-[10px] uppercase tracking-widest text-white/40 font-bold">4 formas de conectar</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { icon: HandHeart, l: "Apoyo",   c: "#38BDF8", d: "Compartir el proceso" },
            { icon: Users,     l: "Amistad", c: "#FBBF24", d: "Sumar gente sobria" },
            { icon: Heart,     l: "Amor",    c: "#FF6B5E", d: "Vínculos reales" },
            { icon: Sparkles,  l: "Grupos",  c: "#8B5CF6", d: "Comunidad de tu país" },
          ].map((it, i) => (
            <motion.div
              key={it.l}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.06 }}
              className="ps-card p-4"
            >
              <it.icon size={20} style={{ color: it.c }} />
              <p className="mt-2 font-display font-bold text-[15px]">{it.l}</p>
              <p className="mt-0.5 text-[11px] text-white/50 leading-tight">{it.d}</p>
            </motion.div>
          ))}
        </div>

        {/* Prueba social + info relevante */}
        <div className="mt-10 space-y-3">
          <div className="ps-card p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#4ADE80]/15 border border-[#4ADE80]/30 grid place-items-center shrink-0">
              <Video size={18} strokeWidth={2} className="text-[#4ADE80]"/>
            </div>
            <div>
              <p className="font-display font-bold text-[14px] leading-tight">Reuniones online semanales</p>
              <p className="mt-0.5 text-[12px] text-white/60 leading-snug">Círculos de conversación abiertos a toda la región. Cámara opcional.</p>
            </div>
          </div>
          <div className="ps-card p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#8B5CF6]/15 border border-[#8B5CF6]/30 grid place-items-center shrink-0">
              <Sprout size={18} strokeWidth={2} className="text-[#8B5CF6]"/>
            </div>
            <div>
              <p className="font-display font-bold text-[14px] leading-tight">Herramientas para tu proceso</p>
              <p className="mt-0.5 text-[12px] text-white/60 leading-snug">Contador de días privado, autoevaluación y apoyo profesional cuando lo necesites.</p>
            </div>
          </div>
        </div>

        <p className="mt-10 text-center text-xs text-white/40 leading-relaxed">
          Solo para mayores de 18 años. PlanSobrio no reemplaza tratamiento profesional ni atención de urgencia.
        </p>
        <p data-testid="sinadicciones-credit" className="mt-4 text-center text-xs text-white/50">
          Hecho con 💛 desde <a href="https://sinadicciones.org" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white underline">SinAdicciones.org</a>
        </p>
        <div className="mt-3 text-center text-xs text-white/40 space-x-3">
          <Link to="/terminos" data-testid="footer-terms" className="hover:text-white/70 underline decoration-transparent hover:decoration-current transition">Términos</Link>
          <span className="opacity-30">·</span>
          <Link to="/privacidad" data-testid="footer-privacy" className="hover:text-white/70 underline decoration-transparent hover:decoration-current transition">Privacidad</Link>
        </div>
      </div>
    </div>
  );
}
